/** Pure rules for adding several source files at once. */
export const ALLOWED_EXT_RE = /\.(txt|md|pdf|docx|pptx|csv|json|png|jpe?g|gif|webp)$/i;
/** Non-PDF/image files are sent inline (base64) and capped at this size. */
export const MAX_INLINE_FILE_BYTES = 6 * 1024 * 1024;
/** Files prepared in the background at the same time. */
export const WARMUP_CONCURRENCY = 4;

export interface BatchFile {
  name: string;
  size: number;
  type: string;
}

export interface BatchResult<F extends BatchFile> {
  accepted: { file: F; usesGemini: boolean }[];
  rejected: { file: F; reason: string }[];
}

export function partitionSourceFiles<F extends BatchFile>(
  files: F[],
  usesGeminiFor: (file: F) => boolean,
): BatchResult<F> {
  const out: BatchResult<F> = { accepted: [], rejected: [] };
  for (const file of files) {
    if (!ALLOWED_EXT_RE.test(file.name) && !file.type.startsWith("image/")) {
      out.rejected.push({ file, reason: "unsupported type" });
      continue;
    }
    const usesGemini = usesGeminiFor(file);
    if (!usesGemini && file.size > MAX_INLINE_FILE_BYTES) {
      out.rejected.push({ file, reason: `over ${MAX_INLINE_FILE_BYTES / 1024 / 1024} MB` });
      continue;
    }
    out.accepted.push({ file, usesGemini });
  }
  return out;
}

/** Run tasks with at most `limit` in flight; failures don't stop the rest. */
export async function runPool<T>(items: T[], limit: number, task: (item: T) => Promise<void>) {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next++];
      try {
        await task(item);
      } catch {
        /* task reports its own errors */
      }
    }
  });
  await Promise.all(workers);
}
